import { ChangeEvent, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import BlogPost from '@/models/blog-post';
import { useBlogPosts } from '@/services/blog';
import BlogModal from './components/blog-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';

const valueFor = (row: BlogPost, key: string): string | number => {
    switch (key) {
        case 'category':
            // `category` is the resolved object — sort by its display name.
            return row.category?.name ?? '';
        default: {
            const v = (row as unknown as Record<string, unknown>)[key];
            return (v as string | number) ?? '';
        }
    }
};

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    return (a: BlogPost, b: BlogPost) => {
        const av = valueFor(a, orderBy);
        const bv = valueFor(b, orderBy);
        if (av < bv) return order === 'desc' ? 1 : -1;
        if (av > bv) return order === 'desc' ? -1 : 1;
        return 0;
    };
};

const Blog = () => {
    const { data: posts, isLoading } = useBlogPosts();
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState('publishedAt');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filtered, setFiltered] = useState<BlogPost[]>(posts?.data || []);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState<string>();

    useEffect(() => {
        if (posts?.data) {
            setFiltered(posts.data);
        }
    }, [posts]);

    const toggle = () => setIsOpen((prev) => !prev);

    const handleRequestAdd = () => {
        setSelectedPostId(undefined);
        toggle();
    };

    const handleRequestSort = (_: unknown, property: string) => {
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }
        setOrder(orderTemp);
        setOrderBy(property);
    };

    const handleClick = (id: string) => {
        setSelectedPostId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => setPage(item);
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Blog</h3>
                    <h3 className="page-subhead subhead">
                        Marketing-site blog posts. Saving publishes to the site immediately; the social-share preview updates on the next production deploy.
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Title or Slug"
                                            type="text"
                                            onChange={(e) => {
                                                const q = e.target.value.toLowerCase();
                                                setFiltered(
                                                    posts?.data?.filter((p) => p.title.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)) || [],
                                                );
                                            }}
                                        />
                                        <MatTableToolbar onRequestAdd={handleRequestAdd} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filtered
                                                    .slice()
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((d) => (
                                                        <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                            <TableCell component="th" scope="row" padding="none">
                                                                {d.slug}
                                                            </TableCell>
                                                            <TableCell>{d.title}</TableCell>
                                                            <TableCell>{d.category?.name ?? ''}</TableCell>
                                                            <TableCell>{d.publishedAt ? formatDate(d.publishedAt) : ''}</TableCell>
                                                            <TableCell>{formatDate(d.updated_at)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                {filtered.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center">
                                                            No posts yet. Add one to get started.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filtered.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <BlogModal isOpen={isOpen} toggle={toggle} selectedPostId={selectedPostId} />
        </Container>
    );
};

export default Blog;
